import { useResourcesBySource, useServerLog } from '@/api'
import { resourceKeys } from '@/api/hooks/keys'
import { useAppSelector } from '@/app'
import { CALENDAR_LAST_HOUR } from '@/app/constants'
import {
  CalendarColumns,
  RouteHeaderContainer,
} from '@/features/calendar/components'
import { calendarModelMapper } from '@/features/calendar/domain'
import { useAutoscroll } from '@/features/calendar/util'
import { selectors as coreSelectors } from '@/features/core'
import { LinearProgress, Paper } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ServiceVisitList } from '../calendar/components/ServiceVisitList'
import { removeChildrenFilter } from '../calendar/domain/serviceVisitModel'
import { useStyles } from './EmployeeCalendar.style'

interface Props {
  id: string
  date: string
  scale?: number
  gutterInterval?: number
  legacyGrouping?: boolean
}
export const EmployeeCalendar = ({
  id,
  date,
  scale = 1,
  gutterInterval = 30,
  legacyGrouping = false,
}: Props) => {
  const { classes } = useStyles()
  const { hideInactiveVisits } = useAppSelector(coreSelectors.selectFlags)
  const {
    isLoading,
    data: visits,
    dataUpdatedAt: visitsUpdatedAt,
  } = useResourcesBySource('ServiceVisit', 'Employee', id, date, date, {
    select: data =>
      data
        .filter(it => it.data.state !== 'Deleted')
        .filter(it =>
          hideInactiveVisits
            ? !calendarModelMapper.mapServiceVisit(it).isInactive()
            : true
        )
        .filter(removeChildrenFilter),
    refetchOnWindowFocus: 'always',
    refetchInterval: 5000,
  })

  const serverLog = useServerLog()
  const queryClient = useQueryClient()
  const [lastLog, setLastLog] = useState(0)
  const [lastCancelled, setLastCancelled] = useState(0)

  useEffect(() => {
    const timeoutId = setInterval(() => {
      const now = new Date().getTime()
      const visitAge = now - visitsUpdatedAt

      if (document.hasFocus() && visitsUpdatedAt > 0 && visitAge > 30000) {
        if (lastLog === 0 || now - lastLog > 60000) {
          setLastLog(now)
          console.debug(`Visit data is too old (${visitAge} ms), refetching...`)
          serverLog.warn(`Visit data is too old (${visitAge / 1000} seconds)`)
        }
        if (lastCancelled === 0 || now - lastCancelled > 2 * 30000) {
          serverLog.warn(
            `Cancelling stale query (${visitAge / 1000} seconds) for Employee ${id} on ${date}, last cancelled ${lastCancelled === 0 ? 0 : (now - lastCancelled) / 1000} seconds ago`
          )
          const key = resourceKeys('ServiceVisit').bySource(
            'Employee',
            id,
            date,
            date,
            []
          )
          ;(async () => {
            try {
              await queryClient.cancelQueries({ queryKey: key })
              queryClient.invalidateQueries({ queryKey: key })
              await queryClient.refetchQueries({
                queryKey: key,
                type: 'active',
              })
            } catch (e) {
              console.warn('Failed to cancel/refetch ServiceVisit query', e)
              serverLog.warn('Failed to cancel/refetch ServiceVisit query')
            } finally {
              setLastCancelled(now)
            }
          })()
        }
      }
    }, 1000)
    return () => clearInterval(timeoutId)
  }, [
    date,
    id,
    lastCancelled,
    lastLog,
    queryClient,
    serverLog,
    visitsUpdatedAt,
  ])

  const items = (visits ?? []).map(it => ({
    position: it.data.from,
    size: it.data.duration,
    item: it,
  }))
  const { data: dayRouteHeaders } = useResourcesBySource(
    'DayRouteHeader',
    'Employee',
    id,
    date,
    date
  )

  const { ref: scrollContainerRef } = useAutoscroll(date, items, scale)
  return (
    <>
      <LinearProgress
        variant={isLoading ? 'indeterminate' : 'determinate'}
        value={100}
      />
      <Paper elevation={3} className={classes.container}>
        <div ref={scrollContainerRef} className={classes.routeContainer}>
          <RouteHeaderContainer
            routes={
              dayRouteHeaders?.map(d => ({
                id: d.data.route,
                date: d.data.date ?? '',
              })) ?? []
            }
          />
          <CalendarColumns
            scale={scale}
            totalMinutes={CALENDAR_LAST_HOUR * 60}
            legacyGrouping={legacyGrouping}
            gutterInterval={gutterInterval}
          >
            <ServiceVisitList
              visits={visits ?? []}
              scale={scale}
              legacyLayout={legacyGrouping}
            />
          </CalendarColumns>
        </div>
      </Paper>
    </>
  )
}
